import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { formatTime } from '@/utils/timeUtils';
import clsx from 'clsx';
import { Clock } from 'lucide-react';

interface FloatingClockProps {
  visible?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export const FloatingClock: React.FC<FloatingClockProps> = ({ 
  visible = true,
  position = 'top-right'
}) => {
  const { secondsRemaining, currentRound, isBreak, isRunning, pauseCycle } = useSelector(
    (state: RootState) => state.clock
  );
  const tournament = useSelector((state: RootState) => state.tournament.current);
  const { clockPosition } = useSelector((state: RootState) => state.settings);

  if (!visible || clockPosition !== 'floating' || !tournament) {
    return null;
  }

  const isPaused = !isRunning;
  const showPauseIndicator = isPaused && pauseCycle < 2;
  const isWarning = secondsRemaining <= 60 && secondsRemaining > 0;
  const isCritical = secondsRemaining <= 30 && secondsRemaining > 0;

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  const roundData = tournament.rounds[currentRound - 1];

  return (
    <div className={clsx(
      'fixed z-50 bg-gray-900/95 border border-gray-600 rounded-lg shadow-2xl',
      'backdrop-blur-sm min-w-[200px]',
      positionClasses[position]
    )}>
      {/* Header */}
      <div className="bg-poker-gold text-black px-3 py-1 rounded-t-md flex items-center gap-2">
        <Clock size={16} />
        <span className="font-bold text-sm">
          {isBreak ? 'BREAK' : `ROUND ${currentRound}`}
        </span>
      </div>

      <div className="p-3 text-white">
        {/* Time Display */}
        <div className={clsx(
          'text-2xl font-mono font-bold text-center mb-2 transition-all duration-300',
          {
            'text-red-400 animate-pulse': isCritical,
            'text-yellow-400': isWarning && !isCritical,
            'text-white': !isWarning,
            'opacity-50': showPauseIndicator,
          }
        )}>
          {formatTime(secondsRemaining)}
        </div>

        {showPauseIndicator && (
          <div className="text-center text-red-400 text-sm font-bold animate-pulse mb-2">
            PAUSED
          </div>
        )}

        {/* Blinds (if not in break) */}
        {roundData && !isBreak && (
          <div className="text-xs text-center space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-400">SB:</span>
              <span className="text-poker-gold font-medium">
                {tournament.settings.currencySymbol}{roundData.smallBlind.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">BB:</span>
              <span className="text-poker-gold font-medium">
                {tournament.settings.currencySymbol}{roundData.bigBlind.toLocaleString()}
              </span>
            </div>
            {roundData.ante > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Ante:</span>
                <span className="text-poker-gold font-medium">
                  {tournament.settings.currencySymbol}{roundData.ante.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Progress indicator */}
        <div className="mt-2">
          <div className="w-full bg-gray-700 rounded-full h-1">
            <div
              className="bg-poker-gold h-1 rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(100, (currentRound / tournament.rounds.length) * 100)}%`
              }}
            />
          </div>
          <div className="text-xs text-gray-400 text-center mt-1">
            {currentRound}/{tournament.rounds.length}
          </div>
        </div>
      </div>
    </div>
  );
};